import { NavLink, Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import {
  Banner,
  Breadcrumb,
  DropdownMenu,
  LoadingButton,
  MoreButton,
  PageTitle,
  Text,
  formatId,
} from '@ingrid/luterkort-react';

import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';
import { AppRoute } from '~src/main/routes';
import { isBookingFailedResponse, isCancelFailedResponse, shouldShowStatusReason } from '~src/utils/status-helpers';

import { useShipmentActions } from '../hooks';
import {
  useShipmentBookParcelsMutation,
  useShipmentCancelMutation,
  useShipmentSchedulePickupMutation,
} from '../mutations';
import { useShipmentMetadataPolling, useTrackingEventsPolling } from '../polling';
import { ShipmentDetails } from '../types';

export interface DetailsHeaderProps {
  shipment: ShipmentDetails;
}

export function Header({ shipment }: DetailsHeaderProps) {
  const schedulePickupMutation = useShipmentSchedulePickupMutation();
  const bookMutation = useShipmentBookParcelsMutation();
  const cancelMutation = useShipmentCancelMutation();
  const tracking = useTracking();

  const actions = useShipmentActions({
    shipment: { status: shipment.status.type, direction_type: shipment.direction_type },
    exclude: ['FREIGHT_DOCUMENTS', 'PRINT_LABEL', 'SHOW_DETAILS'],
  });
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams();

  const shipmentMetadataPolling = useShipmentMetadataPolling();
  const trackingEventsPolling = useTrackingEventsPolling();

  const bookShipment = async () => {
    try {
      await bookMutation.mutateAsync({ shipment_id: id });
      tracking.capture(TrackingEventEnum.ShipmentBookingCompleted, { shipmentId: id });
    } catch (error) {
      tracking.capture(TrackingEventEnum.ShipmentBookingFailed, { shipmentId: id });

      if (isBookingFailedResponse(error)) {
        await shipmentMetadataPolling.mutateAsync({
          variables: id,
          pollUntil: data => data?.status === 'INVALID_BOOKING',
        });
      }

      return;
    }

    const metadataPromise = shipmentMetadataPolling.mutateAsync({
      variables: id,
      pollUntil: data => data?.status === 'BOOKED',
    });

    const trackingEventsPromise = trackingEventsPolling.mutateAsync({
      variables: shipment.deliveries.map(d => d.id!),
      pollUntil: data => data?.length === shipment.deliveries.length,
    });

    await Promise.all([metadataPromise, trackingEventsPromise]);

    toast({
      variant: 'success',
      title: t('shipments.details.bookSuccess'),
    });
  };

  const schedulePickup = async () => {
    try {
      await schedulePickupMutation.mutateAsync({ id: id });
      tracking.capture(TrackingEventEnum.ShipmentSchedulePickupCompleted, { shipmentId: id });
    } catch (e) {
      tracking.capture(TrackingEventEnum.ShipmentSchedulePickupFailed, { shipmentId: id });
      throw e;
    }

    toast({
      variant: 'success',
      title: t('shipments.details.bookPickupSuccess'),
    });

    const metadataPromise = shipmentMetadataPolling.mutateAsync({
      variables: id,
      pollUntil: data => data?.status === 'PICKUP_BOOKED' || data?.status === 'PICKUP_SCHEDULED',
    });

    const trackingEventsPromise = trackingEventsPolling.mutateAsync({
      variables: shipment.deliveries.map(d => d.id!),
      pollUntil: data =>
        data?.filter(({ logistic_units }) => {
          const lastEvent = logistic_units?.[0].tracking_events?.slice(-1).pop();
          return (
            lastEvent?.status === 'PICKUP_BOOKING_SCHEDULED' ||
            lastEvent?.status === 'PICKUP_BOOKED' ||
            lastEvent?.status === 'ORDER_BOOKED'
          );
        }).length === data?.length,
    });

    await Promise.all([metadataPromise, trackingEventsPromise]);
  };

  const cancelShipment = async () => {
    const promise = async () => {
      try {
        await cancelMutation.mutateAsync({ shipment_id: id });
        tracking.capture(TrackingEventEnum.ShipmentCancelCompleted, { shipmentId: id });

        await shipmentMetadataPolling.mutateAsync({
          variables: id,
          pollUntil: data => data?.status === 'CANCELED',
        });
      } catch (e) {
        tracking.capture(TrackingEventEnum.ShipmentCancelFailed, { shipmentId: id });

        if (isCancelFailedResponse(e)) {
          await shipmentMetadataPolling.mutateAsync({
            variables: id,
            pollUntil: data => data?.status === 'CANCEL_FAILED',
          });

          return Promise.reject(new Error('failed to cancel'));
        }

        throw e;
      }
    };

    await toast.promise(promise, {
      loading: { title: t('shipments.details.cancelLoading') },
      error: { title: t('shipments.details.cancelError') },
      success: { title: t('shipments.details.cancelSuccess') },
    });
  };

  const cancelAndDuplicate = async () => {
    const promise = cancelMutation.mutateAsync({ shipment_id: id });

    await toast.promise(promise, {
      loading: { title: t('shipments.details.cancelLoading') },
      error: { title: t('shipments.details.cancelError') },
      success: { title: t('shipments.details.cancelSuccess') },
    });

    navigate(`../${AppRoute.DUPLICATE}/${id}`);
  };

  const mutation = bookMutation.isError
    ? bookMutation
    : schedulePickupMutation.isError
      ? schedulePickupMutation
      : cancelMutation;

  return (
    <>
      <PageTitle.Wrapper>
        <PageTitle.Stack>
          <Breadcrumb aria-label={t('common.breadcrumbs')}>
            <NavLink to="..">{t('common.shipments')}</NavLink>
            <span aria-current="page">{formatId(id)}</span>
          </Breadcrumb>
        </PageTitle.Stack>

        {actions.includes('BOOK') && (
          <RoleGuard roles={['transport:edit']} withTooltip>
            <LoadingButton
              loading={bookMutation.isPending || shipmentMetadataPolling.isPending || trackingEventsPolling.isPending}
              onClick={bookShipment}
            >
              {t('common.book')}
            </LoadingButton>
          </RoleGuard>
        )}

        {actions.includes('SCHEDULE_PICKUP') && (
          <RoleGuard roles={['transport:edit']} withTooltip>
            <LoadingButton
              loading={
                schedulePickupMutation.isPending || shipmentMetadataPolling.isPending || trackingEventsPolling.isPending
              }
              onClick={schedulePickup}
            >
              {t('common.schedulePickup')}
            </LoadingButton>
          </RoleGuard>
        )}

        {actions.length > 0 && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <MoreButton variant="secondary" />
            </DropdownMenu.Trigger>

            <DropdownMenu.Content>
              {actions.includes('EDIT') && (
                <RoleGuard roles={['transport:edit']} withTooltip>
                  <DropdownMenu.Item asChild>
                    <RouterLink to={`../${AppRoute.EDIT}/${id}`}>{t('common.edit')}</RouterLink>
                  </DropdownMenu.Item>
                </RoleGuard>
              )}

              {actions.includes('CREATE_RETURN') && (
                <RoleGuard roles={['transport:edit']} withTooltip>
                  <DropdownMenu.Item asChild>
                    <RouterLink to={`../${AppRoute.RETURN}/${id}`}>{t('common.createReturn')}</RouterLink>
                  </DropdownMenu.Item>
                </RoleGuard>
              )}

              {actions.includes('DUPLICATE') && (
                <RoleGuard roles={['transport:edit']} withTooltip>
                  <DropdownMenu.Item asChild>
                    <RouterLink to={`../${AppRoute.DUPLICATE}/${id}`}>{t('common.duplicate')}</RouterLink>
                  </DropdownMenu.Item>
                </RoleGuard>
              )}

              {actions.includes('CANCEL_AND_DUPLICATE') && (
                <RoleGuard roles={['transport:edit']} withTooltip>
                  <DropdownMenu.Item onClick={cancelAndDuplicate}>{t('common.cancelAndDuplicate')}</DropdownMenu.Item>
                </RoleGuard>
              )}

              {actions.includes('CANCEL') && (
                <RoleGuard roles={['transport:edit']} withTooltip>
                  <DropdownMenu.Item onClick={cancelShipment}>
                    <Text variant="critical">{t('common.cancel')}</Text>
                  </DropdownMenu.Item>
                </RoleGuard>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}
      </PageTitle.Wrapper>

      {/*
        If there's a a shipment status message and it should be shown, that should be prioritized since it contains more
        information about the actual error. Otherwise we show the mutation error, which only contains a breif description
        of the error, at most.
      */}
      {shouldShowStatusReason(shipment.status.type, shipment.status.message) ? (
        <Banner title={t('errors.generic')} subtitle={shipment.status.message} variant="critical" />
      ) : (
        <MutationBanner mutation={mutation} />
      )}
    </>
  );
}
